// File: frontend/src/components/chat/ChatWidget.js

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import MarkdownRenderer from './MarkdownRenderer';
import styles from './ChatWidget.module.css';
import websocketService from '../../services/websocketService';
import { getStoredIds } from '../../utils/bootstrap';
import { API_CONFIG } from '../../config/api';

const ChatWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [currentStreamingId, setCurrentStreamingId] = useState(null);
  const [isStartingNewChat, setIsStartingNewChat] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const hasConnectedOnce = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  };

  useEffect(() => { scrollToBottom(); }, [messages, streamingMessage]);

  useEffect(() => {
    if (isOpen) setTimeout(scrollToBottom, 100);
  }, [isOpen]);

  useEffect(() => {
    if (messages.length > 0) scrollToBottom();
  }, [messages.length]);

  // Scroll lock when chat is open on mobile
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    };
  }, [isOpen]);

  // Listen for global chat open events
  useEffect(() => {
    const handleOpenChat = () => setIsOpen(true);
    window.addEventListener('openChat', handleOpenChat);
    window.addEventListener('openChatWidget', handleOpenChat);
    return () => {
      window.removeEventListener('openChat', handleOpenChat);
      window.removeEventListener('openChatWidget', handleOpenChat);
    };
  }, []);

  // Load chat state from localStorage on mount
  useEffect(() => {
    const savedChatId = localStorage.getItem('chat_id');
    const savedMessages = localStorage.getItem('chat_messages');
    if (savedChatId) setChatId(savedChatId);
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        setMessages(parsed);
        if (parsed.length > 0) setTimeout(scrollToBottom, 150);
      } catch (error) {
        console.error('[ChatWidget] Failed to parse saved messages:', error);
      }
    }
  }, []);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) localStorage.setItem('chat_messages', JSON.stringify(messages));
  }, [messages]);

  // Save chatId to localStorage
  useEffect(() => {
    if (chatId) localStorage.setItem('chat_id', chatId);
  }, [chatId]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  // Initialize WebSocket connection when chat opens
  useEffect(() => {
    if (isOpen && !isConnected) connectWebSocket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Hide on certain dashboard pages
  const hiddenPaths = ['/dashboard/chat', '/dashboard/upload', '/dashboard/policies'];
  if (
    hiddenPaths.some(path => location.pathname.startsWith(path)) ||
    location.pathname.match(/^\/dashboard\/policy-details\//)
  ) {
    return null;
  }

  const connectWebSocket = () => {
    const ids = getStoredIds();
    if (!ids.userId) {
      console.error('[ChatWidget] No user ID found. Bootstrap may have failed.');
      return;
    }

    websocketService.connect(
      ids.userId,
      API_CONFIG.enterpriseId,
      // onOpen
      () => {
        setIsConnected(true);
        if (!hasConnectedOnce.current) {
          hasConnectedOnce.current = true;
          if (!chatId) {
            setIsStartingNewChat(true);
            startChat(ids.userId);
          }
        }
      },
      // onMessage
      (data) => handleWebSocketMessage(data),
      // onClose
      () => {
        setIsConnected(false);
      },
      // onError
      (error) => console.error('[ChatWidget] WebSocket error:', error)
    );
  };

  const handleReconnect = () => {
    websocketService.disconnect();
    websocketService.reconnectAttempts = 0;
    hasConnectedOnce.current = false;
    setTimeout(() => connectWebSocket(), 100);
  };

  const startChat = (userId) => {
    const message = {
      action: 'startChat',
      data: {
        user_id: userId,
        enterprise_id: API_CONFIG.enterpriseId,
        agent_name: 'ai_allegoryinsurance_web_widget'
      }
    };
    websocketService.send(message);
  };

  const startNewChat = () => {
    setChatId(null);
    setMessages([]);
    setStreamingMessage('');
    setCurrentStreamingId(null);
    setIsStartingNewChat(true);
    localStorage.removeItem('chat_id');
    localStorage.removeItem('chat_messages');
    const ids = getStoredIds();
    if (ids.userId && isConnected) {
      startChat(ids.userId);
    } else {
      console.error('[ChatWidget] Cannot start new chat: not connected or no user ID');
    }
  };

  const handleWebSocketMessage = (data) => {
    if (data.success && data.data?.chat_id && data.message === 'Chat session started successfully') {
      setChatId(data.data.chat_id);
      setIsStartingNewChat(false);
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: "Hi! I'm your Allegory Insurance assistant. I can help you understand your policies, file claims, and answer any insurance questions. How can I help you today?",
        timestamp: new Date().toISOString()
      }]);
      setTimeout(scrollToBottom, 100);
      return;
    }

    if (data.type === 'stream_chunk') {
      setIsTyping(true);
      setCurrentStreamingId(data.message_id);
      setStreamingMessage(prev => prev + data.chunk);
      return;
    }

    if (data.type === 'stream_completed') {
      setIsTyping(false);
      setMessages(prev => [...prev, {
        id: data.message_id,
        role: 'assistant',
        content: data.content,
        timestamp: data.timestamp,
        tokens: data.tokens_used
      }]);
      setStreamingMessage('');
      setCurrentStreamingId(null);
      setTimeout(scrollToBottom, 50);
      return;
    }
  };

  const sendMessage = () => {
    if (!inputValue.trim() || !chatId || !isConnected) return;
    if (!websocketService.isConnected()) {
      setIsConnected(false);
      return;
    }
  
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue,
      timestamp: new Date().toISOString()
    };
  
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setTimeout(scrollToBottom, 50);
  
    websocketService.send({
      action: 'sendMessage',
      data: {
        chat_id: chatId,
        message: inputValue,
        enterprise_id: API_CONFIG.enterpriseId
      }
    });
    setIsTyping(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleChat = () => setIsOpen(!isOpen);

  const chatWindow = isOpen && (
    <div className={styles.chatWindow}>
      {/* Header */}
      <div className={styles.chatHeader}>
        <div className={styles.chatHeaderInfo}>
          <span className={styles.chatHeaderTitle}>Allegory Insurance</span>
          <span className={styles.headerDivider} />
          <span className={styles.statusBadge}>
            <span className={`${styles.statusDot} ${isConnected ? styles.statusOnline : ''}`} />
            {isConnected ? 'Online' : 'Offline'}
          </span>
        </div>
        <div className={styles.chatHeaderActions}>
          {!isConnected && (
            <button
              className={styles.headerBtn}
              onClick={handleReconnect}
              title="Reconnect"
              aria-label="Reconnect to chat"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
            </button>
          )}
          {chatId && (
            <button
              className={styles.headerBtn}
              onClick={startNewChat}
              title="New conversation"
              disabled={isStartingNewChat}
              aria-label="Start new conversation"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
          )}
          <button
            className={styles.headerBtn}
            onClick={toggleChat}
            aria-label="Close chat"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className={styles.chatMessages} data-lenis-prevent>
        {isStartingNewChat && messages.length === 0 && (
          <div className={`${styles.message} ${styles.messageAssistant}`}>
            <div className={styles.typingIndicator}>
              <span /><span /><span />
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`${styles.message} ${msg.role === 'user' ? styles.messageUser : styles.messageAssistant}`}
          >
            <div className={styles.messageContent}>
              {msg.role === 'assistant' ? <MarkdownRenderer content={msg.content} /> : msg.content}
            </div>
            <div className={styles.messageTime}>
              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        ))}

        {streamingMessage && (
          <div className={`${styles.message} ${styles.messageAssistant}`}>
            <div className={styles.messageContent}>
              <MarkdownRenderer content={streamingMessage} />
              <span className={styles.cursor} />
            </div>
          </div>
        )}

        {isTyping && !streamingMessage && !isStartingNewChat && (
          <div className={`${styles.message} ${styles.messageAssistant}`}>
            <div className={styles.typingIndicator}>
              <span /><span /><span />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={styles.chatInput}>
        <textarea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={isStartingNewChat ? "Starting conversation..." : "Type your message..."}
          rows="1"
          disabled={!isConnected || (!chatId && !isStartingNewChat)}
          aria-label="Message input"
        />
        <button
          onClick={sendMessage}
          disabled={!inputValue.trim() || !isConnected || !chatId}
          aria-label="Send message"
          className={styles.sendButton}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <button
        className={`${styles.chatButton} ${isOpen ? styles.chatButtonHidden : ''}`}
        onClick={toggleChat}
        aria-label="Open chat"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <span className={styles.chatButtonText}>Ask AI</span>
      </button>
      {createPortal(<>{chatWindow}</>, document.body)}
    </>
  );
};

export default ChatWidget;